import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import {
  connectionRequestSchema,
  connectionSchema,
  type Connection,
} from "./schemas";

/** A CONNECT this person filed that the trigger has not answered yet. */
export interface PendingConnect {
  id: string;
  code: string;
  eventId: string;
  /** Still on this device: the network was gone when it was filed. */
  queued: boolean;
}

export type ConnectionsState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; connections: Connection[] };

/**
 * The person's connections, newest meeting first, read live so a scan on
 * another device or by the other person shows up without a reload.
 */
export function useConnections(uid: string): ConnectionsState {
  const [state, setState] = useState<ConnectionsState>({ status: "loading" });

  useEffect(() => {
    return onSnapshot(
      collection(db, "users", uid, "connections"),
      (snapshot) => {
        const connections: Connection[] = [];
        snapshot.forEach((item) => {
          const parsed = connectionSchema.safeParse(item.data());
          if (parsed.success) connections.push(parsed.data);
        });
        connections.sort(
          (a, b) => b.updatedAt.toMillis() - a.updatedAt.toMillis(),
        );
        setState({ status: "success", connections });
      },
      (error) => setState({ status: "error", error: error.message }),
    );
  }, [uid]);

  return state;
}

/** A CONNECT that left the pending list without connecting anybody. */
export interface FailedConnect {
  id: string;
  code: string;
  error: string;
}

/** What the server refusing a queued write, or a vanished request, reads as. */
export const REFUSED_ON_SYNC =
  "Could not connect. The event may no longer allow connections, or you are no longer registered for it.";

/**
 * CONNECT requests still waiting, including ones queued offline, and the ones
 * that stopped waiting without connecting. The rules let a person list their
 * own requests only, which is exactly this query; offline, the SDK answers it
 * from the cache with the unsent writes in it.
 *
 * A request that leaves the list is read once more. SUCCEEDED needs no word:
 * the connection shows up in the list. FAILED carries the trigger's reason. A
 * queued write the rules refused on sync is dropped from the cache, so the
 * read finds nothing; that also gets a message, so a scan made offline never
 * vanishes silently.
 */
export function usePendingConnects(uid: string): {
  pending: PendingConnect[];
  failed: FailedConnect[];
  dismiss: (id: string) => void;
} {
  const [pending, setPending] = useState<PendingConnect[]>([]);
  const [failed, setFailed] = useState<FailedConnect[]>([]);

  useEffect(() => {
    let seen = new Map<string, PendingConnect>();
    let active = true;
    const settle = async (item: PendingConnect) => {
      let error: string | null = REFUSED_ON_SYNC;
      try {
        const snapshot = await getDoc(doc(db, "connection_requests", item.id));
        if (snapshot.exists()) {
          const parsed = connectionRequestSchema.safeParse(snapshot.data());
          if (parsed.success && parsed.data.status === "FAILED") {
            error = parsed.data.error ?? REFUSED_ON_SYNC;
          } else if (parsed.success) {
            error = null;
          }
        }
      } catch {
        // Unreadable means there is no such document for this person.
      }
      if (active && error) {
        setFailed((current) => [
          ...current.filter((entry) => entry.id !== item.id),
          { id: item.id, code: item.code, error },
        ]);
      }
    };
    const stop = onSnapshot(
      query(
        collection(db, "connection_requests"),
        where("requestedBy", "==", uid),
        where("status", "==", "REQUESTED"),
      ),
      { includeMetadataChanges: true },
      (snapshot) => {
        const next = new Map<string, PendingConnect>();
        snapshot.forEach((item) => {
          const parsed = connectionRequestSchema.safeParse(item.data());
          if (
            !parsed.success ||
            parsed.data.type !== "CONNECT" ||
            !parsed.data.code ||
            !parsed.data.eventId
          ) {
            return;
          }
          next.set(item.id, {
            id: item.id,
            code: parsed.data.code,
            eventId: parsed.data.eventId,
            queued: item.metadata.hasPendingWrites,
          });
        });
        for (const [id, item] of seen) {
          if (!next.has(id)) void settle(item);
        }
        seen = next;
        setPending([...next.values()]);
      },
      () => setPending([]),
    );
    return () => {
      active = false;
      stop();
    };
  }, [uid]);

  const dismiss = useCallback(
    (id: string) =>
      setFailed((current) => current.filter((entry) => entry.id !== id)),
    [],
  );

  return { pending, failed, dismiss };
}
